import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../logo-cb-courtage.png';

const NAVY = '#1a2b4a';
const GOLD = '#c8a96e';
const LIGHT = '#f8fafc';

// ── Navbar ──────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? 'rgba(26,43,74,0.97)' : NAVY,
      backdropFilter: scrolled ? 'blur(8px)' : 'none',
      boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.18)' : 'none',
      transition: 'all 0.25s',
      padding: '0 32px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      height: '68px',
    }}>
      <img src={logo} alt="CB Courtage" style={{ height: '40px', cursor: 'pointer' }} onClick={() => scrollTo('hero')} />

      {/* Burger mobile */}
      <button onClick={() => setMenuOpen(o => !o)} style={{
        display: 'none', background: 'none', border: 'none', cursor: 'pointer',
        color: '#fff', fontSize: '22px',
        ['@media (max-width:768px)']: { display: 'block' }
      }} aria-label="Menu">☰</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
        {['hero', 'services', 'apropos', 'contact'].map((id, i) => (
          <button key={id} onClick={() => scrollTo(id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#fff', fontSize: '14px', fontWeight: '500', opacity: 0.9,
          }}>
            {['Accueil', 'Nos services', 'À propos', 'Contact'][i]}
          </button>
        ))}
        <button onClick={() => navigate('/login')} style={{
          background: GOLD, color: NAVY, border: 'none', borderRadius: '6px',
          padding: '8px 20px', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
          transition: 'opacity 0.15s',
        }}
          onMouseEnter={e => e.target.style.opacity = '0.85'}
          onMouseLeave={e => e.target.style.opacity = '1'}>
          Espace client
        </button>
      </div>
    </nav>
  );
}

// ── Simulateur ──────────────────────────────────────────────────────────────
function Simulateur() {
  const [montant, setMontant] = useState(50000);
  const [duree, setDuree] = useState(48);
  const [vr, setVr] = useState(10);

  const taux = 0.045;
  const tm = taux / 12;
  const base = montant * (1 - vr / 100);
  const loyer = base > 0 && duree > 0
    ? base * (tm / (1 - Math.pow(1 + tm, -duree)))
    : 0;

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: `1.5px solid #dde3ef`,
    borderRadius: '8px', fontSize: '15px', color: NAVY, outline: 'none',
    transition: 'border-color 0.15s',
  };

  return (
    <section style={{ background: LIGHT, padding: '72px 24px' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '700', color: NAVY, marginBottom: '8px' }}>
          Simulateur de loyer
        </h2>
        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '40px', fontSize: '15px' }}>
          Estimez votre loyer mensuel en quelques secondes — taux indicatif 4,5 % annuel
        </p>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '36px', boxShadow: '0 4px 24px rgba(26,43,74,0.08)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '32px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                Montant HT (€)
              </label>
              <input type="number" value={montant} min="1000" step="1000"
                onChange={e => setMontant(+e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = GOLD}
                onBlur={e => e.target.style.borderColor = '#dde3ef'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                Durée (mois)
              </label>
              <select value={duree} onChange={e => setDuree(+e.target.value)} style={inputStyle}>
                {[24, 36, 48, 60, 72].map(d => <option key={d} value={d}>{d} mois</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                Valeur résiduelle (%)
              </label>
              <select value={vr} onChange={e => setVr(+e.target.value)} style={inputStyle}>
                {[0, 5, 10, 15, 20].map(v => <option key={v} value={v}>{v} %</option>)}
              </select>
            </div>
          </div>
          <div style={{ background: NAVY, borderRadius: '12px', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Loyer mensuel estimé
              </div>
              <div style={{ color: GOLD, fontSize: '38px', fontWeight: '800', letterSpacing: '-1px' }}>
                {loyer > 0 ? loyer.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €' : '—'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '4px' }}>
                HT · taux indicatif · sans engagement
              </div>
            </div>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ background: GOLD, color: NAVY, border: 'none', borderRadius: '8px', padding: '12px 28px', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>
              Demander une étude →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Home ─────────────────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nom: '', telephone: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [contactError, setContactError] = useState('');

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    setContactError('');
    try {
      const res = await fetch('https://crm.cbcourtage.fr/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: form.nom, telephone: form.telephone }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = typeof data.detail === 'string'
          ? data.detail
          : `Erreur ${res.status}`;
        throw new Error(msg);
      }
      setSent(true);
    } catch (err) {
      setContactError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  const services = [
    {
      icon: '🏦',
      titre: 'Financement professionnel',
      desc: 'Crédit-bail, location financière, prêt professionnel — nous trouvons la solution adaptée à vos projets d\'investissement.',
    },
    {
      icon: '🚗',
      titre: 'Assurance flotte auto',
      desc: 'Protection complète de votre parc de véhicules professionnels, gestion des sinistres et optimisation des contrats.',
    },
    {
      icon: '📋',
      titre: 'Accompagnement dossier',
      desc: 'Constitution et suivi de vos dossiers de financement, de la demande à la signature, avec un interlocuteur dédié.',
    },
  ];

  const chiffres = [
    { val: '+200', label: 'clients accompagnés' },
    { val: '10 ans', label: "d'expérience" },
    { val: '15 M€', label: 'financés' },
    { val: '98 %', label: 'de satisfaction' },
  ];

  const temoignages = [
    {
      texte: '« CB Courtage a géré notre dossier de financement avec une rapidité et un professionnalisme remarquables. En moins de 3 semaines, notre flotte était renouvelée. »',
      auteur: 'Marc D.',
      poste: 'Gérant, Transport MD',
    },
    {
      texte: '« Grâce à leur expertise, nous avons obtenu un financement que notre banque habituelle nous avait refusé. Je recommande sans hésitation. »',
      auteur: 'Sophie L.',
      poste: 'Directrice, BTP Léonard',
    },
  ];

  const fonctionnalites = ['Suivi de vos dossiers de financement', 'Gestion de vos contrats assurance', 'Déclaration de sinistres en ligne', 'Accès à votre parc de véhicules'];

  return (
    <div>
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section id="hero" style={{
        minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY} 0%, #243a5e 60%, #1e3055 100%)`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '120px 24px 80px',
      }}>
        <div style={{ maxWidth: '760px' }}>
          <div style={{ display: 'inline-block', background: 'rgba(200,169,110,0.15)', border: `1px solid rgba(200,169,110,0.35)`, borderRadius: '100px', padding: '6px 18px', marginBottom: '28px' }}>
            <span style={{ color: GOLD, fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px' }}>Courtier en financement & assurance professionnelle</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: '800', lineHeight: '1.15', marginBottom: '20px', letterSpacing: '-1px' }}>
            Votre partenaire en financement<br />
            <span style={{ color: GOLD }}>et assurance professionnelle</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '18px', lineHeight: '1.65', marginBottom: '40px' }}>
            CB Courtage vous accompagne dans tous vos projets d'investissement et protège votre activité avec des solutions sur-mesure.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => document.getElementById('simulateur')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ background: GOLD, color: NAVY, border: 'none', borderRadius: '8px', padding: '14px 32px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', transition: 'opacity 0.15s' }}
              onMouseEnter={e => e.target.style.opacity = '0.85'}
              onMouseLeave={e => e.target.style.opacity = '1'}>
              Simuler mon financement
            </button>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: '8px', padding: '14px 32px', fontWeight: '600', fontSize: '16px', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.1)'}>
              Nous contacter
            </button>
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: '36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
          <span>Défiler</span>
          <span style={{ fontSize: '18px' }}>↓</span>
        </div>
      </section>

      {/* ── Simulateur ──────────────────────────────────────────── */}
      <div id="simulateur"><Simulateur /></div>

      {/* ── Services ─────────────────────────────────────────────── */}
      <section id="services" style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '700', color: NAVY, marginBottom: '8px' }}>Nos services</h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '15px', marginBottom: '48px' }}>Des solutions complètes pour votre activité professionnelle</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {services.map(s => (
              <div key={s.titre} style={{ background: LIGHT, borderRadius: '16px', padding: '32px 28px', borderTop: `4px solid ${GOLD}`, transition: 'box-shadow 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(26,43,74,0.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ fontSize: '36px', marginBottom: '16px' }}>{s.icon}</div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: NAVY, marginBottom: '10px' }}>{s.titre}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.65' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Chiffres clés ─────────────────────────────────────────── */}
      <section id="apropos" style={{ background: NAVY, padding: '72px 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '700', color: '#fff', marginBottom: '8px' }}>CB Courtage en chiffres</h2>
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: '15px', marginBottom: '48px' }}>La confiance de nos clients, nos meilleurs atouts</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '24px' }}>
            {chiffres.map(c => (
              <div key={c.label} style={{ textAlign: 'center', padding: '24px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: '12px' }}>
                <div style={{ color: GOLD, fontSize: '36px', fontWeight: '800', marginBottom: '8px' }}>{c.val}</div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Témoignages ───────────────────────────────────────────── */}
      <section style={{ padding: '80px 24px', background: LIGHT }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '700', color: NAVY, marginBottom: '48px' }}>Ce que disent nos clients</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {temoignages.map(t => (
              <div key={t.auteur} style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 2px 16px rgba(26,43,74,0.07)', borderLeft: `4px solid ${GOLD}` }}>
                <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', fontStyle: 'italic', marginBottom: '20px' }}>{t.texte}</p>
                <div style={{ fontWeight: '700', color: NAVY, fontSize: '14px' }}>{t.auteur}</div>
                <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '2px' }}>{t.poste}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Banner espace client ───────────────────────────────────── */}
      <section style={{ background: `linear-gradient(135deg, #0f1f3d 0%, ${NAVY} 100%)`, padding: '72px 24px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '32px' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>Accédez à votre espace client</h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', marginBottom: '20px' }}>Retrouvez en un clic toutes vos informations :</p>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {fonctionnalites.map(f => (
                <li key={f} style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: GOLD }}>✓</span> {f}
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => navigate('/login')} style={{
            background: GOLD, color: NAVY, border: 'none', borderRadius: '8px',
            padding: '16px 36px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
            whiteSpace: 'nowrap', transition: 'opacity 0.15s',
          }}
            onMouseEnter={e => e.target.style.opacity = '0.85'}
            onMouseLeave={e => e.target.style.opacity = '1'}>
            Se connecter →
          </button>
        </div>
      </section>

      {/* ── Contact ───────────────────────────────────────────────── */}
      <section id="contact" style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '700', color: NAVY, marginBottom: '8px' }}>Nous contacter</h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '15px', marginBottom: '40px' }}>Un conseiller vous répond sous 24h</p>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '48px', background: LIGHT, borderRadius: '16px' }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
              <div style={{ fontWeight: '700', color: NAVY, fontSize: '18px', marginBottom: '8px' }}>Message envoyé !</div>
              <div style={{ color: '#64748b', fontSize: '14px' }}>Nous reviendrons vers vous très prochainement.</div>
            </div>
          ) : (
            <form onSubmit={handleContact} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { key: 'nom',       label: 'Nom complet',        type: 'text', required: true },
                { key: 'telephone', label: 'Numéro de téléphone', type: 'tel',  required: true },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{f.label}</label>
                  <input type={f.type} required value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', outline: 'none', transition: 'border-color 0.15s', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = GOLD}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </div>
              ))}
              {contactError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px' }}>
                  {contactError}
                </div>
              )}
              <button type="submit" disabled={sending} style={{ background: sending ? '#94a3b8' : NAVY, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontWeight: '700', fontSize: '15px', cursor: sending ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => { if (!sending) e.target.style.background = '#243a5e'; }}
                onMouseLeave={e => { if (!sending) e.target.style.background = NAVY; }}>
                {sending ? 'Envoi en cours…' : 'Être rappelé'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer style={{ background: '#0f1f3d', padding: '32px 24px', textAlign: 'center' }}>
        <img src={logo} alt="CB Courtage" style={{ height: '32px', marginBottom: '16px', opacity: 0.85 }} />
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
          © 2026 CB Courtage — Tous droits réservés &nbsp;·&nbsp;
          <span style={{ color: GOLD, cursor: 'pointer' }}>Mentions légales</span>
        </p>
      </footer>
    </div>
  );
}
